import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Stack,
  Text,
  Textarea,
  List,
  Paper,
  Divider,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { formatStaffLabel, getEntityLabel } from "../../data/mcaEntities";
import { hasMcaEntityForm } from "./mcaEntityFormRegistry";
import McaResubmitFormModal from "./McaResubmitFormModal";
import { hasPayloadContent, mergeProposedEntity } from "./mcaPayloadHelpers";

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const McaRejectionPanel = ({
  request,
  process,
  isMaker,
  revisedPayload,
  onRevisedPayloadChange,
  onResubmit,
  resubmitting = false,
}) => {
  const [makerReply, setMakerReply] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  const lastRejection = request?.lastRejection;
  const action = request?.action;
  const canRevise = action !== "DELETE" && hasMcaEntityForm(request?.targetEntity);

  const mergedInitialData = useMemo(() => {
    if (action === "CREATE") {
      return revisedPayload ?? request?.payload ?? {};
    }
    return mergeProposedEntity(
      request?.originalData,
      revisedPayload ?? request?.payload,
      request?.targetEntityId
    );
  }, [action, request, revisedPayload]);

  const priorRounds = useMemo(() => {
    const history = request?.rejectionHistory ?? [];
    if (history.length) return history;
    return (request?.logs ?? [])
      .filter((log) => ["STEP_REJECTED", "RESUBMITTED"].includes(log.action))
      .slice(0, -1);
  }, [request]);

  const handleSaveDraft = (payload) => {
    onRevisedPayloadChange(payload);
    setDraftSaved(true);
    setFormOpen(false);
    showNotification({
      title: "Changes saved",
      message: "Ready to resubmit for approval",
      color: "green",
    });
  };

  const handleResubmit = async () => {
    if (!hasPayloadContent(revisedPayload) && action !== "DELETE") {
      showNotification({
        title: "No changes",
        message: "Revise the request before resubmitting",
        color: "red",
      });
      return;
    }
    await onResubmit({
      payload: revisedPayload,
      comment: makerReply.trim() || undefined,
    });
  };

  if (!lastRejection) return null;

  const approverLabel =
    formatStaffLabel(lastRejection.rejectedBy) ?? "Approver";

  return (
    <Alert color="red" title="Request rejected">
      <Stack spacing="md">
        {priorRounds.length > 0 && (
          <Stack spacing="xs">
            <Text size="sm" weight={600}>
              Prior rounds
            </Text>
            {priorRounds.map((entry, idx) => (
              <Paper key={idx} p="sm" withBorder>
                <Text size="xs" color="dimmed">
                  {entry.stepName ?? `Step ${(entry.step ?? 0) + 1}`} ·{" "}
                  {formatDateTime(entry.rejectedAt ?? entry.at)}
                </Text>
                {entry.comment && (
                  <Text size="sm" mt={4}>
                    {entry.comment}
                  </Text>
                )}
              </Paper>
            ))}
            <Divider />
          </Stack>
        )}

        <Stack spacing={4}>
          <Text size="sm" color="dimmed">
            Rejected by {approverLabel} on{" "}
            {formatDateTime(lastRejection.rejectedAt)}
            {lastRejection.stepName
              ? ` · ${lastRejection.stepName}`
              : lastRejection.step != null
              ? ` · Step ${lastRejection.step + 1}`
              : ""}
          </Text>
          <Text size="sm">{lastRejection.comment}</Text>
          {lastRejection.uncheckedItems?.length > 0 && (
            <Stack spacing={4} mt="xs">
              <Text size="sm" weight={600}>
                Unchecked checklist items
              </Text>
              <List size="sm" spacing={4}>
                {lastRejection.uncheckedItems.map((item, idx) => (
                  <List.Item key={idx}>{item}</List.Item>
                ))}
              </List>
            </Stack>
          )}
        </Stack>

        {isMaker && (
          <>
            <Textarea
              label="Your reply to approver"
              placeholder="Optional — explain what you changed or respond to the rejection"
              minRows={3}
              value={makerReply}
              onChange={(e) => setMakerReply(e.currentTarget.value)}
            />

            {action === "DELETE" ? (
              <Alert color="yellow">
                Deletion requests cannot be revised. Cancel this request if you
                no longer want to proceed.
              </Alert>
            ) : canRevise ? (
              <Group>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => {
                    setDraftSaved(false);
                    setFormOpen(true);
                  }}
                >
                  Revise changes
                </Button>
                <Button
                  size="sm"
                  onClick={handleResubmit}
                  loading={resubmitting}
                  disabled={!hasPayloadContent(revisedPayload)}
                >
                  Resubmit for approval
                </Button>
              </Group>
            ) : (
              <Text size="sm" color="dimmed">
                This entity cannot be revised from MCA. Update the record
                directly or contact an administrator.
              </Text>
            )}

            {draftSaved && hasPayloadContent(revisedPayload) && (
              <Text size="sm" color="teal">
                Changes saved — ready to resubmit
              </Text>
            )}

            <McaResubmitFormModal
              opened={formOpen}
              onClose={() => setFormOpen(false)}
              request={request}
              process={process}
              initialData={mergedInitialData}
              onSave={handleSaveDraft}
            />
          </>
        )}

        {!isMaker && (
          <Text size="sm" color="dimmed">
            {getEntityLabel(request?.targetEntity)} · {action}
          </Text>
        )}
      </Stack>
    </Alert>
  );
};

export default McaRejectionPanel;
